import DigiobcanApi, {
  type IssueStatement,
} from '../../../../api/DigiobcanApi';
import { useM } from '../../../../lib/query';
import { useLogger } from '../../../../providers/LoggerProvider';
import { __, toastError, toastSuccess } from '../../../../utils';
import {
  Button,
  Stack,
  TextField,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  /** Vyjadrenie v schvaľovaní - `approval` je `pending` alebo `rejected`. */
  statement: IssueStatement;
  /**
   * Smie prihlásený úradník o čakajúcom vyjadrení rozhodnúť? Počíta to server
   * (`awaiting_my_approval` na náleze): `can_approve` alebo `manager`
   * v kategórii nálezu, aj keď je autorom - rozhoduje príznak, nie autorstvo.
   * Neskladá sa tu, aby sa tlačidlá nerozišli s filtrom „Na schválenie",
   * ktorý počíta ten istý výraz.
   */
  canReview: boolean;
  /** Smie vrátené vyjadrenie stiahnuť (autor, schvaľovateľ, správca)? */
  canWithdraw: boolean;
  /**
   * Platí prihlásenému v kategórii nálezu `requires_approval`? „Poslať znova"
   * sa riadi **aktuálnym** príznakom: bez neho API upravené vyjadrenie
   * zverejní hneď, takže tlačidlo nesmie sľubovať schválenie.
   */
  requiresApproval: boolean;
  /**
   * Podnet je archivovaný alebo nález nepatrí do kategórie prihláseného.
   * API by každú akciu odmietlo, takže sa ani neponúka.
   */
  readOnly?: boolean;
  compact?: boolean;
  onDone: () => Promise<unknown>;
};

type Action = 'approve' | 'reject' | 'resubmit' | 'withdraw';

/**
 * `published` je len pri „poslať znova": keď autorovi medzitým vypli
 * `requires_approval`, API upravené vyjadrenie zverejní hneď a na schválenie
 * ho neposiela.
 */
const SUCCESS: Record<Action, (final: boolean, published: boolean) => string> =
  {
    approve: (final) =>
      final
        ? __('Vyjadrenie bolo schválené a nález vyriešený.')
        : __('Vyjadrenie bolo schválené a zverejnené.'),
    reject: () => __('Vyjadrenie bolo vrátené autorovi na úpravu.'),
    resubmit: (final, published) =>
      !published
        ? __('Vyjadrenie bolo znova odoslané na schválenie.')
        : final
          ? __(
              'Schválenie už nepotrebujete - vyjadrenie bolo zverejnené a nález vyriešený.',
            )
          : __('Schválenie už nepotrebujete - vyjadrenie bolo zverejnené.'),
    withdraw: () => __('Vyjadrenie bolo stiahnuté.'),
  };

/** Žltý rám bloku - to isté, čím je vyjadrenie v schvaľovaní vyznačené všade. */
export const FRAME_SX = {
  p: 1.5,
  borderRadius: 1,
  border: (theme) => `1px solid ${theme.palette.warning.surface}`,
  backgroundColor: 'warning.surface',
} as const;

/**
 * Vyjadrenie v schvaľovaní - tri pohľady na tú istú vec:
 *
 *  * **schvaľovateľ** pri čakajúcom: text je rovno upraviteľný (preklep
 *    netreba vracať), poznámka pre autora, „Schváliť" a „Vrátiť na úpravu",
 *  * **autor** pri vrátenom: pripomienka schvaľovateľa, jeho vlastný text na
 *    úpravu a „Odoslať znova" - je to **to isté vyjadrenie**, nie nové, takže
 *    predošlé kolo zostáva v histórii a schvaľovateľ vidí, čo sa zmenilo,
 *  * **ostatní**: len informácia, prečo sa nález nedá ďalej riešiť, prípadne
 *    „Stiahnuť" pre schvaľovateľa, keby autor vrátené nechal visieť.
 */
const StatementApproval: React.FC<Props> = ({
  statement,
  canReview,
  canWithdraw,
  requiresApproval,
  readOnly = false,
  compact = false,
  onDone,
}) => {
  const { logger } = useLogger();
  const [text, setText] = React.useState(statement.text);
  const [note, setNote] = React.useState('');

  // Iné vyjadrenie alebo nové kolo je nový koncept - rozpísaná úprava
  // k nemu nepatrí.
  React.useEffect(() => {
    setText(statement.text);
    setNote('');
  }, [statement.statement_id, statement.text, statement.approval]);

  const rejected = statement.approval === 'rejected';
  const lastReview = statement.reviews?.[statement.reviews.length - 1];
  const edited = text.trim() !== statement.text;

  const mutation = useM(
    async (action: Action) => {
      const id = statement.statement_id;
      let published = false;

      if (action === 'resubmit') {
        const res = await DigiobcanApi.adminResubmitStatement(
          id,
          {},
          { text: text.trim() },
        );
        published = !!res.published;
      } else if (action === 'withdraw') {
        await DigiobcanApi.adminWithdrawStatement(id);
      } else {
        await DigiobcanApi.adminReviewStatement(
          id,
          {},
          {
            decision: action,
            ...(action === 'approve' && edited && { text: text.trim() }),
            ...(note.trim() && { note: note.trim() }),
          },
        );
      }

      await onDone();
      return { action, published };
    },
    {
      onSuccess: ({ action, published }) =>
        toastSuccess(SUCCESS[action](statement.final, published)),
      onError: (error, action) => {
        logger.error('Failed to change statement in review', {
          action: 'statementReview',
          error,
          data: { statementId: statement.statement_id, change: action },
        });

        const name = error.details?.name;
        if (
          name === 'STATEMENT_NOT_PENDING' ||
          name === 'STATEMENT_NOT_REJECTED'
        ) {
          toastError(
            __(
              'S vyjadrením už medzitým niekto niečo urobil. Obnovte stránku.',
            ),
          );
        } else if (name === 'FORBIDDEN_STATEMENT_APPROVAL') {
          toastError(__('Vyjadrenia v tejto kategórii neschvaľujete.'));
        } else if (name === 'REPORT_ARCHIVED') {
          toastError(__('Podnet je archivovaný.'));
        } else {
          toastError(__('Zmenu sa nepodarilo uložiť.'));
        }
      },
    },
  );

  const busy = mutation.isPending;

  const buttonsSx = {
    direction: compact ? ('column' as const) : ('row' as const),
    alignItems: compact ? 'stretch' : 'center',
  };

  // Pripomienka schvaľovateľa k vrátenému - autor ju potrebuje mať pred očami
  // pri úprave, nie o blok vyššie v histórii.
  const returnedNote = rejected && lastReview?.note && (
    <Typography
      variant='body2'
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        borderLeft: (theme) => `3px solid ${theme.palette.warning.main}`,
        backgroundColor: 'background.paper',
      }}
    >
      {lastReview.note}
    </Typography>
  );

  // Znenie pre toho, kto ho nemení - autor pri čakajúcom, ostatní pri
  // čakajúcom aj vrátenom. Bez neho bolo vidieť len to, *že* niečo čaká,
  // a čo, sa dalo zistiť až rozbalením histórie.
  const readOnlyText = (
    <Stack
      gap={0.5}
      sx={{ p: 1.25, borderRadius: 1, backgroundColor: 'background.paper' }}
    >
      <Typography variant='body2' sx={{ whiteSpace: 'pre-line' }}>
        {statement.text}
      </Typography>
      {statement.media.map((media) => (
        <Typography
          key={media.media_id}
          variant='caption'
          component='a'
          href={media.public_url}
          target='_blank'
          rel='noopener noreferrer'
        >
          {media.original_filename ?? media.media_id}
        </Typography>
      ))}
    </Stack>
  );

  // ── Autor upravuje vrátené ────────────────────────────────────────────────
  if (rejected && statement.mine && !readOnly) {
    return (
      <Stack gap={1.25} sx={FRAME_SX}>
        <Stack gap={0.25}>
          <Typography variant='body2' color='warning.dark' fontWeight={700}>
            {__('Vyjadrenie vám vrátili na úpravu')}
          </Typography>
          <Typography variant='caption' color='warning.dark'>
            {requiresApproval
              ? __(
                  'Upravte ho podľa pripomienky a pošlite znova. Občan ho zatiaľ nevidí.',
                )
              : statement.final
                ? __(
                    'Schválenie už nepotrebujete - po úprave sa vyjadrenie zverejní a nález sa vyrieši.',
                  )
                : __(
                    'Schválenie už nepotrebujete - po úprave sa vyjadrenie hneď zverejní.',
                  )}
          </Typography>
        </Stack>

        {returnedNote}

        <TextField
          multiline
          minRows={compact ? 4 : 3}
          fullWidth
          value={text}
          onChange={(event) => setText(event.target.value)}
          sx={{ backgroundColor: 'background.paper' }}
        />

        <Stack gap={1} {...buttonsSx}>
          <Button
            fullWidth={compact}
            disabled={busy || !text.trim()}
            onClick={() => mutation.mutate('resubmit')}
          >
            {requiresApproval
              ? __('Odoslať znova na schválenie')
              : statement.final
                ? __('Zverejniť a vyriešiť')
                : __('Zverejniť')}
          </Button>
          <Button
            variant='text'
            fullWidth={compact}
            disabled={busy}
            onClick={() => mutation.mutate('withdraw')}
          >
            {__('Stiahnuť vyjadrenie')}
          </Button>
        </Stack>
      </Stack>
    );
  }

  // ── Vrátené, pohľad ostatných ─────────────────────────────────────────────
  if (rejected) {
    return (
      <Stack gap={0.75} sx={FRAME_SX}>
        <Typography variant='body2' color='warning.dark' fontWeight={500}>
          {__('Vyjadrenie je vrátené autorovi na úpravu')}
        </Typography>
        <Typography variant='caption' color='warning.dark'>
          {__(
            'Kým ho autor neupraví a nepošle znova, ďalšie vyjadrenie sa pridať ani nález vyriešiť nedá.',
          )}
        </Typography>
        {readOnlyText}
        {returnedNote}
        {canWithdraw && !readOnly && (
          <Stack gap={1} {...buttonsSx}>
            <Button
              variant='text'
              fullWidth={compact}
              disabled={busy}
              onClick={() => mutation.mutate('withdraw')}
            >
              {__('Stiahnuť vyjadrenie')}
            </Button>
          </Stack>
        )}
      </Stack>
    );
  }

  const title = statement.final
    ? __('Finálne vyjadrenie čaká na schválenie')
    : __('Vyjadrenie čaká na schválenie');

  // ── Čaká, pohľad toho, kto neschvaľuje ────────────────────────────────────
  if (!canReview || readOnly) {
    return (
      <Stack gap={0.75} sx={FRAME_SX}>
        <Typography variant='body2' color='warning.dark' fontWeight={500}>
          {title}
        </Typography>
        <Typography variant='caption' color='warning.dark'>
          {statement.mine
            ? __(
                'Občan ho uvidí až po schválení. Kým o ňom schvaľovateľ nerozhodne, ďalšie vyjadrenie sa pridať nedá.',
              )
            : __(
                'Schváliť ho môže schvaľovateľ alebo správca kategórie. Kým o ňom nerozhodne, ďalšie vyjadrenie sa pridať ani nález vyriešiť nedá.',
              )}
          {statement.final &&
            ` ${__('Nález sa vyrieši až schválením, lehota dovtedy beží.')}`}
        </Typography>
        {readOnlyText}
      </Stack>
    );
  }

  // ── Čaká, schvaľovateľ rozhoduje ──────────────────────────────────────────
  return (
    <Stack gap={1.25} sx={FRAME_SX}>
      <Stack gap={0.25}>
        <Typography variant='body2' color='warning.dark' fontWeight={700}>
          {title}
        </Typography>
        <Typography variant='caption' color='warning.dark'>
          {statement.final
            ? __(
                'Schválením sa zverejní ako finálne stanovisko úradu a nález sa vyrieši.',
              )
            : __(
                'Schválením sa zverejní pri podnete a občan o ňom dostane správu.',
              )}
        </Typography>
      </Stack>

      <TextField
        multiline
        minRows={compact ? 4 : 3}
        fullWidth
        value={text}
        onChange={(event) => setText(event.target.value)}
        helperText={
          edited
            ? __('Zverejní sa upravené znenie, pôvodné ostane v histórii.')
            : undefined
        }
        sx={{ backgroundColor: 'background.paper' }}
      />

      <TextField
        multiline
        minRows={2}
        fullWidth
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder={__(
          'Pripomienka pre autora (nepovinné pri schválení, občan ju neuvidí)…',
        )}
        sx={{ backgroundColor: 'background.paper' }}
      />

      <Stack gap={1} {...buttonsSx}>
        <Button
          fullWidth={compact}
          disabled={busy || !text.trim()}
          onClick={() => mutation.mutate('approve')}
        >
          {edited ? __('Upraviť a schváliť') : __('Schváliť')}
        </Button>
        <Button
          variant='text'
          fullWidth={compact}
          disabled={busy}
          onClick={() => mutation.mutate('reject')}
        >
          {__('Vrátiť na úpravu')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default StatementApproval;
